// Phase 4: reconcile the committed projection with QuickBooks actuals for the
// active year. Pure module (no DB, no app). See REVENUE_ENGINE_SPEC.md section 6.
//
//   Closed months  -> QuickBooks actuals
//   Projected months:
//     recurring / AUM      -> committed projection
//     tax prep / tax plan  -> (committed annual - actuals to date) / remaining months
//                             (committed annual is held FIXED; a sync never moves it)
//     commissions          -> 0 (actuals-only)
//   BTS split: QuickBooks lumps bookkeeping + CAO + tax prep into one "Tax
//   Preparation" account, so bookkeeping and CAO use their committed monthly amounts
//   and tax prep is the residual (QB combined minus bookkeeping minus CAO).

import { engagementContribution } from './engine.mjs';

export const PL_CATEGORIES = ['subscription_fees', 'investment_management', 'tax_planning', 'tax_preparation', 'bookkeeping', 'cao', 'commissions'];

// engine category -> P&L category key
const plCat = (c) => (c === 'subscription' ? 'subscription_fees' : c);

// Committed monthly projection per P&L category: { cat: number[12] }.
export function committedByCategory(engagements, year) {
  const out = {};
  for (const k of PL_CATEGORIES) out[k] = new Array(12).fill(0);
  for (const e of engagements) {
    const { months } = engagementContribution(e, year);
    const key = plCat(e.category);
    if (!out[key]) out[key] = new Array(12).fill(0);
    for (let m = 0; m < 12; m++) out[key][m] += months[m];
  }
  return out;
}

// Reconcile committed projection with QB actuals. Returns { byCategory: {cat:[12]},
// committedAnnual: {tax_preparation, tax_planning} } for the given active year.
export function reconcileYear(engagements, qbActuals, cutoff, year) {
  const committed = committedByCategory(engagements, year);
  const projMonths = 12 - cutoff;
  const actual = (cat, m) => (qbActuals[cat] && Number(qbActuals[cat][m])) || 0;
  const out = {};

  // Actuals override closed months, committed drives projected months.
  for (const cat of ['subscription_fees', 'investment_management']) {
    out[cat] = committed[cat].map((v, m) => (m < cutoff ? actual(cat, m) : v));
  }
  // Bookkeeping and CAO have no separate QB account: committed for every month.
  out.bookkeeping = committed.bookkeeping.slice();
  out.cao = committed.cao.slice();
  // Commissions: actuals for closed months, not projected.
  out.commissions = committed.commissions.map((v, m) => (m < cutoff ? actual('commissions', m) : 0));

  // Committed-absorbed: annual is fixed; remainder spreads evenly over projected.
  const absorb = (cat, closedValueForMonth) => {
    const annual = committed[cat].reduce((s, v) => s + v, 0);
    const arr = new Array(12).fill(0);
    let toDate = 0;
    for (let m = 0; m < cutoff; m++) { arr[m] = closedValueForMonth(m); toDate += arr[m]; }
    const per = projMonths > 0 ? Math.max(0, annual - toDate) / projMonths : 0;
    for (let m = cutoff; m < 12; m++) arr[m] = per;
    return { arr, annual };
  };

  const tp = absorb('tax_preparation', (m) => actual('tax_preparation', m) - out.bookkeeping[m] - out.cao[m]);
  out.tax_preparation = tp.arr;
  const plan = absorb('tax_planning', (m) => actual('tax_planning', m));
  out.tax_planning = plan.arr;

  return { byCategory: out, committedAnnual: { tax_preparation: tp.annual, tax_planning: plan.annual } };
}

export const annualOf = (arr) => arr.reduce((s, v) => s + v, 0);
